/**
 * useAnalytics Hook
 * 
 * Centralizează toate API calls pentru analytics și KPI medici
 * Folosește endpoint-urile noi din Layer 2 (implementate în backend)
 */

import { useState, useEffect, useCallback } from 'react'
import apiClient from '../services/api'

// ==================== INTERFACES ====================

export interface ProductivityStats {
  total_studies: number
  completed_studies: number
  total_images_read: number
  complexity_weighted_volume: number
  studies_per_hour: number
  images_per_hour: number
  modality_distribution: Record<string, number>
  priority_distribution: Record<string, number>
  daily_studies?: Array<{ date: string; count: number }>
}

export interface ActivityStats {
  login_count: number
  total_logged_time_sec: number
  total_active_time_sec: number
  idle_time_sec: number
  last_activity_at?: string
  active_sessions_count: number
  hourly_patterns?: Array<{ hour: number; activity_count: number }>
  peak_hour?: number
}

export interface ReportsStats {
  total_reports: number
  draft_count: number
  finalized_count: number
  avg_length_chars: number
  avg_word_count: number
  avg_writing_time_sec: number
  words_per_minute: number
  avg_time_to_finalize_sec: number
  post_finalize_edits_count: number
}

export interface TrendData {
  score_trend: Array<{ period: string; score: number }>
  volume_trend: Array<{ period: string; studies_count: number }>
  time_trend: Array<{ period: string; avg_time: number }>
  consistency_score: number
}

export interface ContextStats {
  priority_distribution: Record<string, number>
  body_parts_distribution: Record<string, number>
  institutions_count: number
  external_studies_count: number
  second_opinion_count: number
  avg_complexity_score: number
  avg_series_per_study: number
  avg_images_per_study: number
}

export interface BillingStats {
  total_billable_studies: number
  total_revenue: number
  avg_tariff_per_study: number
  body_parts_billed: Record<string, { count: number; total_value: number }>
  batches_generated_count: number
}

// ==================== HOOK ====================

interface UseAnalyticsParams {
  doctorId?: string
  fromDate?: Date
  toDate?: Date
  autoFetch?: boolean
}

export const useAnalytics = (params: UseAnalyticsParams = {}) => {
  const { doctorId, fromDate, toDate, autoFetch = false } = params

  // States
  const [productivity, setProductivity] = useState<ProductivityStats | null>(null)
  const [activity, setActivity] = useState<ActivityStats | null>(null)
  const [reportsStats, setReportsStats] = useState<ReportsStats | null>(null)
  const [trends, setTrends] = useState<TrendData | null>(null)
  const [contextStats, setContextStats] = useState<ContextStats | null>(null)
  const [billingStats, setBillingStats] = useState<BillingStats | null>(null)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Helper pentru query params
  const buildQueryParams = useCallback(() => {
    const params = new URLSearchParams()
    if (fromDate) params.append('from_date', fromDate.toISOString().split('T')[0])
    if (toDate) params.append('to_date', toDate.toISOString().split('T')[0])
    return params.toString()
  }, [fromDate, toDate])

  // Fetch Productivity
  const fetchProductivity = useCallback(async (id?: string) => {
    const targetId = id || doctorId
    if (!targetId) return

    try {
      const query = buildQueryParams()
      const response = await apiClient.get(
        `/analytics/doctor/${targetId}/productivity${query ? `?${query}` : ''}`
      )
      setProductivity(response.data)
      return response.data
    } catch (err: any) {
      console.error('Error fetching productivity:', err)
      throw err
    }
  }, [doctorId, buildQueryParams])

  // Fetch Activity
  const fetchActivity = useCallback(async (id?: string) => {
    const targetId = id || doctorId
    if (!targetId) return

    try {
      const query = buildQueryParams()
      const response = await apiClient.get(
        `/analytics/doctor/${targetId}/activity${query ? `?${query}` : ''}`
      )
      setActivity(response.data)
      return response.data
    } catch (err: any) {
      console.error('Error fetching activity:', err)
      throw err
    }
  }, [doctorId, buildQueryParams])

  // Fetch Reports Stats
  const fetchReportsStats = useCallback(async (id?: string) => {
    const targetId = id || doctorId
    if (!targetId) return

    try {
      const query = buildQueryParams()
      const response = await apiClient.get(
        `/analytics/doctor/${targetId}/reports-stats${query ? `?${query}` : ''}`
      )
      setReportsStats(response.data)
      return response.data
    } catch (err: any) {
      console.error('Error fetching reports stats:', err)
      throw err
    }
  }, [doctorId, buildQueryParams])

  // Fetch Trends
  const fetchTrends = useCallback(async (id?: string) => {
    const targetId = id || doctorId
    if (!targetId) return

    try {
      const query = buildQueryParams()
      const response = await apiClient.get(
        `/analytics/doctor/${targetId}/trends${query ? `?${query}` : ''}`
      )
      setTrends(response.data)
      return response.data
    } catch (err: any) {
      console.error('Error fetching trends:', err)
      throw err
    }
  }, [doctorId, buildQueryParams])

  // Fetch Context Stats
  const fetchContextStats = useCallback(async (id?: string) => {
    const targetId = id || doctorId
    if (!targetId) return

    try {
      const query = buildQueryParams()
      const response = await apiClient.get(
        `/analytics/doctor/${targetId}/context-stats${query ? `?${query}` : ''}`
      )
      setContextStats(response.data)
      return response.data
    } catch (err: any) {
      console.error('Error fetching context stats:', err)
      throw err
    }
  }, [doctorId, buildQueryParams])

  // Fetch Billing Stats
  const fetchBillingStats = useCallback(async (id?: string) => {
    const targetId = id || doctorId
    if (!targetId) return

    try {
      const query = buildQueryParams()
      const response = await apiClient.get(
        `/analytics/doctor/${targetId}/billing-stats${query ? `?${query}` : ''}`
      )
      setBillingStats(response.data)
      return response.data
    } catch (err: any) {
      console.error('Error fetching billing stats:', err)
      throw err
    }
  }, [doctorId, buildQueryParams])

  // Fetch All (pentru refresh complet)
  const fetchAll = useCallback(async (id?: string) => {
    const targetId = id || doctorId
    if (!targetId) {
      setError('Medic ID is required')
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Fetch în paralel pentru performance
      await Promise.all([
        fetchProductivity(targetId),
        fetchActivity(targetId),
        fetchReportsStats(targetId),
        fetchTrends(targetId),
        fetchContextStats(targetId),
        fetchBillingStats(targetId),
      ])
    } catch (err: any) {
      console.error('Error fetching analytics:', err)
      setError(err.response?.data?.detail || 'Eroare la încărcarea datelor analytics')
    } finally {
      setLoading(false)
    }
  }, [doctorId, fetchProductivity, fetchActivity, fetchReportsStats, fetchTrends, fetchContextStats, fetchBillingStats])

  // Auto-fetch on mount dacă autoFetch = true
  useEffect(() => {
    if (autoFetch && doctorId) {
      fetchAll()
    }
  }, [autoFetch, doctorId, fetchAll])

  return {
    // Data
    productivity,
    activity,
    reportsStats,
    trends,
    contextStats,
    billingStats,

    // States
    loading,
    error,

    // Actions
    fetchProductivity,
    fetchActivity,
    fetchReportsStats,
    fetchTrends,
    fetchContextStats,
    fetchBillingStats,
    fetchAll,
    refresh: fetchAll,
  }
}

// ==================== HELPER FUNCTIONS ====================

/**
 * Formatare timp în format human-readable
 */
export const formatSeconds = (seconds: number): string => {
  if (!seconds || seconds === 0) return '0s'
  
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`
  }
  if (minutes > 0) {
    return `${minutes}m ${secs}s`
  }
  return `${secs}s`
}

/**
 * Formatare număr cu separatori de mii
 */
export const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('ro-RO').format(num)
}

/**
 * Formatare procent
 */
export const formatPercent = (value: number): string => {
  return `${Math.round(value * 100)}%`
}

